import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { authAPI } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

// Función para verificar si el token es válido
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    // Verificar formato JWT
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    // Decodificar el JWT (sin verificar firma, solo para ver expiración)
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp * 1000; // Convertir a milisegundos
    // Token válido si NO ha expirado (sin buffer de 5 min para evitar problemas)
    return exp > Date.now();
  } catch (e) {
    return false;
  }
};

// Función para limpiar todo el almacenamiento de auth
const clearAllAuthStorage = () => {
  console.log('[Auth] Limpiando todo el almacenamiento de autenticación...');
  localStorage.removeItem('avipork_token');
  localStorage.removeItem('avipork_user');
  localStorage.removeItem('avipork_supabase_auth');
  // Limpiar todas las claves de Supabase
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('sb-') || key.includes('supabase')) {
      localStorage.removeItem(key);
    }
  });
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);

  useEffect(() => {
    // Prevent double initialization
    if (initialized.current) return;
    initialized.current = true;

    // Check for existing session on mount - CON TIMEOUT para evitar loops
    const initAuth = async () => {
      // Timeout de seguridad: si tarda más de 5 segundos, forzar fin de loading
      const timeoutId = setTimeout(() => {
        console.log('[Auth] Timeout de inicialización - forzando fin de loading');
        setLoading(false);
      }, 5000);

      try {
        // First check localStorage for legacy token
        const token = localStorage.getItem('avipork_token');
        const savedUser = localStorage.getItem('avipork_user');
        
        if (token && savedUser) {
          // Verificar si el token sigue siendo válido
          if (isTokenValid(token)) {
            try {
              const parsedUser = JSON.parse(savedUser);
              if (parsedUser && parsedUser.email) {
                setUser(parsedUser);
                clearTimeout(timeoutId);
                setLoading(false);
                return; // Use cached user - AUTH SUCCESS
              }
            } catch (e) {
              console.log('[Auth] Error parsing user, clearing cache');
            }
          } else {
            console.log('[Auth] Token expirado o inválido, limpiando caché...');
          }
          // Token inválido o usuario corrupto - limpiar
          clearAllAuthStorage();
        }
        
        // Try Supabase session SOLO si no hay token legacy válido
        // Con timeout corto para no bloquear la app
        try {
          const sessionPromise = supabase.auth.getSession();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), 3000)
          );
          
          const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);
          
          if (session && session.user) {
            const userData = {
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.email.split('@')[0],
            };
            setUser(userData);
            localStorage.setItem('avipork_token', session.access_token);
            localStorage.setItem('avipork_user', JSON.stringify(userData));
          }
        } catch (supabaseError) {
          console.log('[Auth] Supabase session check failed or timed out:', supabaseError.message);
          // No bloquear si Supabase falla - simplemente no hay sesión
        }
      } catch (error) {
        console.error('[Auth] Error initializing auth:', error);
        // En caso de cualquier error, limpiar todo
        clearAllAuthStorage();
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    initAuth();

    // Listen for Supabase auth changes - pero NO bloquear si falla
    let subscription = null;
    try {
      const { data } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          // Solo manejar eventos explícitos, no refreshes
          if (event === 'SIGNED_IN' && session) {
            const userData = {
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.email.split('@')[0],
            };
            setUser(userData);
            localStorage.setItem('avipork_token', session.access_token);
            localStorage.setItem('avipork_user', JSON.stringify(userData));
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            clearAllAuthStorage();
          } else if (event === 'TOKEN_REFRESHED' && session) {
            localStorage.setItem('avipork_token', session.access_token);
          }
        }
      );
      subscription = data?.subscription;
    } catch (e) {
      console.log('[Auth] Supabase auth listener error (non-blocking):', e);
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    // First try Supabase Auth (silently - don't log errors for legacy users)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!error && data.session) {
        // Supabase auth succeeded
        const userData = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.user_metadata?.name || data.user.email.split('@')[0],
        };
        
        setUser(userData);
        localStorage.setItem('avipork_token', data.session.access_token);
        localStorage.setItem('avipork_user', JSON.stringify(userData));
        
        return userData;
      }
      // Supabase failed - silently fall through to legacy API
      // (No console.log here to avoid noisy 400 errors for legacy users)
    } catch (e) {
      // Silently ignore Supabase errors - will try legacy API next
    }
    
    // Fallback to legacy API
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('avipork_token', access_token);
      localStorage.setItem('avipork_user', JSON.stringify(userData));
      setUser(userData);
      
      return userData;
    } catch (e) {
      // Only throw error if BOTH auth methods failed
      console.error('[Auth] Login failed:', e.response?.data?.detail || e.message);
      throw e;
    }
  };

  const register = async (email, password, name) => {
    // Try Supabase Auth first
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name },
        },
      });

      if (!error && data.session) {
        const userData = {
          id: data.user.id,
          email: data.user.email,
          name: name,
        };
        
        setUser(userData);
        localStorage.setItem('avipork_token', data.session.access_token);
        localStorage.setItem('avipork_user', JSON.stringify(userData));
        
        // Also register in backend
        try {
          await authAPI.register({ email, password, name });
        } catch (e) {
          // Ignore if already exists
        }
        
        return userData;
      }
    } catch (e) {
      console.log('Supabase signup error:', e);
    }
    
    // Fallback to legacy API
    const response = await authAPI.register({ email, password, name });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('avipork_token', access_token);
    localStorage.setItem('avipork_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.log('[Auth] Supabase signout error (non-blocking):', error);
    }
    
    clearAllAuthStorage();
    setUser(null);
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) {
      throw error;
    }
    
    return { message: 'Email de recuperación enviado' };
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    resetPassword,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
